import React, { useEffect, useState, useRef } from 'react';
import { Box, Container as BoxContainer } from '@mui/material';

import DashboardLayout from "../LayoutContainers/DashboardLayout";
import DashboardNavbar from "../Navbars/DashboardNavbar";
import MDBox from "../MDBox";
import Card from "@mui/material/Card";

import Api from '../../Api/Api';
import colors from "../../colors";
import Container from '../Container';
import Results from "./Results";
// import ResultsModal from '../../oldComponents/ResultsModal';
import Question from "./Question";
import { setActiveQuiz, useUserController } from "../../context/user";
import { useMaterialUIController } from "../../context";

function QuizPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const quizId = urlParams.get("quizId");
  const gcid = urlParams.get("gcid");

  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [valid, setValid] = useState(false);
  const [answered, setAnswered] = useState([]);
  const [answerKey, setAnswerKey] = useState([]);
  const [pointValues, setPointValues] = useState([]);
  const [score, setScore] = useState(0);
  const [isGroupCode, setIsGroupCode] = useState(!!gcid);
  const [groupCodeActive, setGroupCodeActive] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [watchBlocked, setWatchBlocked] = useState(false);

  const quizStartTime = useRef(new Date());

  const [controller] = useMaterialUIController();
  const { spanish } = controller;
  const [userController, userDispatch] = useUserController();
  const { user, activeQuiz } = userController;
  const [activeQuizState, setActiveQuizState] = useState(activeQuiz);

  useEffect(() => {
    setActiveQuiz(userDispatch, {quizStartTime: quizStartTime.current, videoId: quizId});
    setActiveQuizState(prev => ({ ...prev, quizStartTime: quizStartTime.current }));
  }, [userDispatch]);

  useEffect(() => {
    if (gcid) {
      fetchGroupCodeQuiz(gcid);
    } else if (quizId) {
      fetchRegularQuiz(quizId);
    }
  }, [gcid, quizId]);

  // Observe-safe video-watch guard for direct quiz links. Only blocks when the server is
  // under enforcement AND certain the video is unverified; FAILS OPEN otherwise (null status,
  // observe mode, unknown duration, or error). The server cert guard is the real enforcement;
  // this just keeps a skipper from landing on the questions with no explanation. The group-code
  // (gcid) path is intentionally untouched.
  useEffect(() => {
    if (!quizId || gcid || !user || !user.id) return;
    Api.getWatchStatus(user.id, quizId)
      .then(({ data }) => setWatchBlocked(!!(data && data.enforcement === "enforce" && data.canProceed === false)))
      .catch(() => setWatchBlocked(false));
  }, [quizId, gcid, user]);

  const fetchGroupCodeQuiz = async (gcid) => {
    try {
      const response = await Api.getGroupCode(gcid);
      const data = response.data[0];
      if (data) {
        const filteredQuestions = data.questions.filter(q => !q.isDeleted);
        data.questions = filteredQuestions;

        setActiveQuiz(userDispatch, {
          videoId: data.id,
          quizStartTime: new Date(),
          isGroupCode: true,
          videoName: data?.english?.title || ''
        });

        setActiveQuizState(prev => ({
          ...prev,
          videoId: data.id,
          quizStartTime: new Date(),
          isGroupCode: true,
          videoName: data?.english?.title || ''
        }));

        setGroupCodeActive(data.groupActive);
        if (data.groupActive) {
          setQuiz(data);
          buildAnswerKey(data);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFetched(true);
    }
  };

  const fetchRegularQuiz = async (quizId) => {
    try {
      const response = await Api.getOneQuiz(quizId);
      const data = response.data[0];
      const filteredQuestions = data.questions.filter(q => !q.isDeleted);
      data.questions = filteredQuestions;
      setQuiz(data);
      buildAnswerKey(data);
    } catch (err) {
      console.error(err);
    } finally {
      setFetched(true);
    }
  };

  const buildAnswerKey = (quiz) => {
    const answers = quiz.questions.map(q => q.answer);
    const points = quiz.questions.map(() => 1);
    setAnswerKey(answers);
    setPointValues(points);
  };

  const setTheAnswers = ({ q, a }) => {
    const updatedAnswers = [...answers];
    const updatedAnswered = [...answered];
    updatedAnswers[q] = a;
    updatedAnswered[q] = true;
    setAnswers(updatedAnswers);
    setAnswered(updatedAnswered);
    validateQuiz(updatedAnswered);
  };

  const validateQuiz = (answered) => {
    const allAnswered = answerKey.every((_, i) => answered[i]);
    setValid(allAnswered);
  };

  const evalQuiz = async () => {
    let total = 0;
    let possible = 0;

    for (let i = 0; i < answers.length; i++) {
      const points = parseInt(pointValues[i]);
      possible += points;
      if (answers[i] === answerKey[i]) total += points;
    }

    const finalScore = (total / possible) * 100;
    setActiveQuiz(userDispatch, {
      score: finalScore,
      quizEndTime: new Date(),
      videoId: quizId

    });
    setScore(finalScore);
    await updateQuizProgress(finalScore);
    return finalScore;
  };

  const updateQuizProgress = async (score) => {
    const endTime = new Date();
    setActiveQuiz(userDispatch, {
      score,
      quizStartTime: quizStartTime.current,
      quizEndTime: endTime,
      videoId: quizId
    });
    setActiveQuizState(prev => ({
      ...prev,
      score,
      quizStartTime: quizStartTime.current,
      quizEndTime: endTime,
      videoId: quizId
    }));
  };

  const renderQuiz = (quiz) => (
    <ul style={{ listStyleType: 'decimal' }}>
      {quiz.questions.map((q, i) => (
        <Question
          key={i}
          type={q.type}
          spanish={spanish}
          question={spanish ? q.spanish : q.english}
          questionID={q.id}
          count={i}
          callback={setTheAnswers}
        />
      ))}
    </ul>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={0}>
        <Card>
          <div style={{ padding: '20px 60px', overflow: 'scroll' }}>
            {quizId && watchBlocked ? (
              <Container title={spanish ? "Ver el video primero" : "Watch the video first"}>
                <Box sx={{ padding: 2 }}>
                  {spanish
                    ? "Debes ver el video de capacitación completo antes de realizar el cuestionario."
                    : "You must watch the full training video before taking this quiz."}
                  {" "}
                  <a href={`/video/${quizId}`}>{spanish ? "Ir al video" : "Go to the video"}</a>
                </Box>
              </Container>
            ) : quiz && fetched ? (
              <Container title={`Quiz: ${spanish ? quiz.spanish.title : quiz.english.title}`}>
                <BoxContainer sx={{ marginTop: 2, marginBottom: 2, backgroundColor: colors.Theme.lightGray, padding: 2, borderRadius: 1 }}>
                  {renderQuiz(quiz)}
                </BoxContainer>
                <Box>
                  {(!isGroupCode || groupCodeActive) && (
                    <Results
                      spanish={spanish}
                      user={user}
                      cb={evalQuiz}
                      dis={!valid}
                      data={quiz}
                      score={score || 0}
                    />
                  )}
                </Box>
              </Container>
            ) : (
              <Container title={gcid ? 'Invalid Group Code.' : 'No Quiz Found.'} />
            )}
          </div>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default React.memo(QuizPage);
